import { useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2, Camera, CheckCircle2, AlertTriangle, ArrowRight, FileText, Trash2 } from "lucide-react";

const CONF_META = {
  high: { label: "Correspondência exata", cls: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800" },
  low:  { label: "Possível correspondência", cls: "text-amber-600 bg-amber-50 border-amber-300 dark:bg-amber-900/10 dark:border-amber-800" },
  none: { label: "Sem correspondência", cls: "text-destructive bg-destructive/5 border-destructive/40" },
};

export default function ExamScannerModal({ patientId, catalog, onClose, onSaved }) {
  const [step, setStep] = useState("upload"); // upload | processing | review | saving
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [collectionDate, setCollectionDate] = useState(new Date().toISOString().slice(0, 10));
  const [dragOver, setDragOver] = useState(false);

  const catalogOptions = useMemo(
    () => (catalog || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [catalog]
  );

  const handleFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    const accepted = [];
    for (const f of arr) {
      if (!validTypes.includes(f.type)) {
        toast.error(`${f.name}: formato inválido.`);
        continue;
      }
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name}: maior que 15 MB.`);
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => {
      const combined = [...prev, ...accepted];
      if (combined.length > 10) {
        toast.warning("Máximo de 10 arquivos por leitura.");
        return combined.slice(0, 10);
      }
      return combined;
    });
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const extract = async () => {
    if (!files.length) return;
    setStep("processing");
    setFileErrors([]);
    const form = new FormData();
    for (const f of files) form.append("files", f);
    try {
      const res = await api.post(`/admin/patients/${patientId}/exam-scan/extract`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const data = res.data || {};
      const list = (data.rows || []).map((r) => ({ ...r, edit_value: r.extracted_value }));
      setRows(list);
      setFileErrors(data.file_errors || []);
      if (list.length === 0) {
        toast.warning("A IA não encontrou nenhum marcador nos arquivos enviados.");
        setStep("upload");
        return;
      }
      const matched = list.filter((r) => r.matched_marker_id).length;
      toast.success(`${list.length} marcador(es) extraído(s) em ${data.files_processed} arquivo(s) — ${matched} identificado(s).`);
      setStep("review");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao processar os exames.");
      setStep("upload");
    }
  };

  const updateRow = (rowId, patch) => {
    setRows((prev) => prev.map((r) => (r.row_id === rowId ? { ...r, ...patch } : r)));
  };

  const importable = rows.filter((r) => r.import && r.matched_marker_id);

  const save = async () => {
    if (importable.length === 0) {
      toast.error("Selecione ao menos um marcador com correspondência para importar.");
      return;
    }
    setStep("saving");
    try {
      const payload = {
        collection_date: collectionDate,
        rows: importable.map((r) => ({
          marker_id: r.matched_marker_id,
          value: Number(r.edit_value),
          collection_date: collectionDate,
        })),
      };
      const res = await api.post(`/admin/patients/${patientId}/exam-scan/confirm`, payload);
      const saved = res.data?.saved_count || 0;
      toast.success(`${saved} marcador(es) integrado(s) à ficha do paciente.`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Falha ao salvar marcadores.");
      setStep("review");
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4"
      onClick={step === "processing" || step === "saving" ? undefined : onClose}
      data-testid="exam-scanner-modal"
    >
      <div
        className="bg-card w-full max-w-4xl rounded-sm border border-border shadow-xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-primary text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-gold/20 grid place-items-center">
              <Camera className="w-5 h-5 text-gold" />
            </div>
            <div>
              <div className="overline text-gold">Scanner de Exame · IA</div>
              <div className="font-heading font-bold">Leitura automática (Gemini 2.5 Flash)</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={step === "processing" || step === "saving"}
            className="p-2 hover:bg-primary-foreground/10 rounded-sm disabled:opacity-40"
            data-testid="scanner-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === "upload" && (
            <div className="space-y-6">
              <label
                htmlFor="scanner-file-input"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                className={`block cursor-pointer rounded-sm border-2 border-dashed p-10 text-center transition-colors ${dragOver ? "border-gold bg-gold/5" : "border-border hover:border-gold hover:bg-muted/40"}`}
                data-testid="scanner-dropzone"
              >
                <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                <div className="font-heading font-semibold text-primary mb-1">
                  Arraste ou clique para enviar exame(s)
                </div>
                <div className="text-xs text-muted-foreground">
                  PNG, JPEG, WEBP ou PDF · até 15 MB por arquivo · até 10 arquivos
                </div>
                <input
                  id="scanner-file-input"
                  type="file"
                  multiple
                  accept="image/png,image/jpeg,image/webp,application/pdf"
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="scanner-file-input"
                />
              </label>

              {files.length > 0 && (
                <div className="space-y-2" data-testid="scanner-file-list">
                  <div className="overline text-muted-foreground">Arquivos selecionados ({files.length})</div>
                  {files.map((f, i) => (
                    <div key={`${f.name}-${i}`} className="flex items-center gap-3 bg-card border border-border rounded-sm px-3 py-2 text-sm">
                      <FileText className="w-4 h-4 text-gold flex-none" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-primary truncate">{f.name}</div>
                        <div className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB · {f.type.split("/")[1]?.toUpperCase() || "?"}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="p-1 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
                        data-testid={`scanner-file-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-3 bg-secondary/40 border border-border rounded-sm p-4 text-xs text-muted-foreground">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-none mt-0.5" />
                <div>
                  A IA extrai <strong>nomes, valores e unidades</strong> de todos os exames enviados e consolida em uma
                  única tabela de revisão. Nenhum dado é gravado sem sua confirmação na próxima etapa.
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={extract}
                  disabled={files.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="scanner-extract-btn"
                >
                  Extrair marcadores <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === "processing" && (
            <div className="py-16 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-6" />
              <div className="font-heading font-semibold text-primary mb-1">Analisando exame…</div>
              <div className="text-xs text-muted-foreground">Isso pode levar até 30 segundos.</div>
            </div>
          )}

          {step === "saving" && (
            <div className="py-16 text-center">
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-6" />
              <div className="font-heading font-semibold text-primary">Salvando marcadores…</div>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-4">
              {fileErrors.length > 0 && (
                <div className="bg-destructive/5 border border-destructive/40 rounded-sm p-3 text-xs" data-testid="scanner-file-errors">
                  <div className="font-semibold text-destructive mb-1">{fileErrors.length} arquivo(s) não processado(s):</div>
                  <ul className="list-disc pl-5 text-destructive/80">
                    {fileErrors.map((fe, i) => (
                      <li key={i}><strong>{fe.file}</strong> — {fe.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  <strong className="text-primary">{importable.length}</strong> de {rows.length} marcador(es) marcado(s) para importar
                </div>
                <div className="flex items-center gap-2">
                  <label className="overline text-muted-foreground">Data da coleta</label>
                  <input
                    type="date"
                    value={collectionDate}
                    onChange={(e) => setCollectionDate(e.target.value)}
                    className="px-2 py-1 rounded-sm border border-input bg-card text-xs"
                    data-testid="scanner-collection-date"
                  />
                </div>
              </div>

              <div className="border border-border rounded-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left w-10">Importar</th>
                      <th className="px-3 py-2 text-left">Extraído (IA)</th>
                      <th className="px-3 py-2 text-left">Correspondência na biblioteca</th>
                      <th className="px-3 py-2 text-left w-32">Valor</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((r) => {
                      const conf = CONF_META[r.confidence] || CONF_META.none;
                      return (
                        <tr key={r.row_id} className="hover:bg-muted/30 transition-colors" data-testid={`scanner-row-${r.row_id}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={!!r.import}
                              disabled={!r.matched_marker_id}
                              onChange={(e) => updateRow(r.row_id, { import: e.target.checked })}
                              className="w-4 h-4 accent-primary"
                              data-testid={`scanner-import-${r.row_id}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-primary">{r.extracted_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {r.extracted_value}{r.extracted_unit ? ` ${r.extracted_unit}` : ""}
                            </div>
                            {r.source_file && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-muted-foreground/80" data-testid={`scanner-source-${r.row_id}`}>
                                <FileText className="w-3 h-3" /> {r.source_file}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={r.matched_marker_id || ""}
                              onChange={(e) => {
                                const m = catalogOptions.find((x) => x.id === e.target.value);
                                updateRow(r.row_id, {
                                  matched_marker_id: e.target.value || null,
                                  matched_marker_name: m?.name || null,
                                  matched_marker_unit: m?.unit || null,
                                  confidence: e.target.value ? "high" : "none",
                                  import: !!e.target.value,
                                });
                              }}
                              className="w-full px-2 py-1 rounded-sm border border-input bg-card text-xs"
                              data-testid={`scanner-marker-select-${r.row_id}`}
                            >
                              <option value="">— Sem correspondência —</option>
                              {catalogOptions.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}{c.unit ? ` (${c.unit})` : ""}</option>
                              ))}
                            </select>
                            <span className={`mt-1 inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${conf.cls}`}>
                              {conf.label}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              step="any"
                              value={r.edit_value}
                              onChange={(e) => updateRow(r.row_id, { edit_value: e.target.value })}
                              className="w-full px-2 py-1 rounded-sm border border-input bg-card text-xs"
                              data-testid={`scanner-value-${r.row_id}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setStep("upload"); setFiles([]); setRows([]); setFileErrors([]); }}
                  className="px-4 py-2 rounded-sm border border-border text-sm hover:bg-muted transition-colors"
                  data-testid="scanner-back-btn"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={importable.length === 0}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="scanner-save-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Importar {importable.length} marcador(es)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
